<template>
  <div class="px-4 py-5">
    <LoadingState :loading="loading" :message="error" />

    <div v-if="app && !loading" class="space-y-4">
      <section class="card-panel p-5 space-y-3 text-left">
        <div class="flex items-start justify-between gap-2">
          <h1 class="text-lg font-extrabold text-slate-800">Job Information</h1>
          <StatusBadge :status="app.status" />
        </div>
        <h2 class="text-base font-bold text-[#0d4169]">{{ getJobEntryTitle({ title: app.job_title }, app.job_entry_index) }}</h2>
        <div class="grid grid-cols-2 gap-2 text-sm">
          <div class="rounded-xl bg-[#e9f2f8] p-3">
            <p class="text-[10px] font-bold uppercase text-[#0d4169]/70">Salary</p>
            <p class="font-bold text-[#0d4169]">{{ entrySalary }}</p>
          </div>
          <div class="rounded-xl bg-slate-50 border border-slate-100 p-3" v-if="1==3">
            <p class="text-[10px] font-bold uppercase text-slate-500">Sector</p>
            <p class="font-bold text-slate-800">{{ entrySector }}</p>
          </div>
        </div>
        <p class="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{{ app.job_description }}</p>
      </section>

      <section class="card-panel p-5 space-y-2 text-left">
        <h2 class="text-lg font-extrabold text-slate-800">My Application</h2>
        <p class="text-sm"><span class="font-semibold">Full Name:</span> {{ app.name }}</p>
        <p class="text-sm"><span class="font-semibold">Phone:</span> {{ app.phone }}</p>
        <p class="text-sm" v-if="app.telegram_username"><span class="font-semibold">Telegram:</span> @{{ app.telegram_username }}</p>
        <p class="text-sm" v-if="app.message"><span class="font-semibold">Message:</span> {{ app.message }}</p>
        <div v-if="cvFiles.length" class="space-y-2 pt-1">
          <p class="text-sm font-semibold">Uploaded files</p>
          <div
            v-for="file in cvFiles"
            :key="file.path"
            class="flex items-start gap-2"
          >
            <a
              v-if="file.isImage"
              :href="file.url"
              target="_blank"
              rel="noopener"
              class="block"
            >
              <img
                :src="file.url"
                :alt="file.name"
                class="h-16 w-16 rounded-md object-cover border border-slate-200"
              />
            </a>
            <a
              :href="file.url"
              target="_blank"
              rel="noopener"
              class="inline-flex text-sm font-bold text-[#0d4169] underline break-all"
            >
              {{ file.name }}
            </a>
          </div>
        </div>
        <p class="text-xs text-slate-500">Applied: {{ formatDate(app.created_at) }}</p>
      </section>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { apiFetch } from '../utils/api'
import { mapCvFilesForDisplay } from '../utils/cvFiles'
import { getJobEntryTitle, splitJobValues } from '../utils/jobFields'
import LoadingState from '../components/LoadingState.vue'
import StatusBadge from '../components/StatusBadge.vue'

export default {
  name: 'ApplicationDetailView',
  components: { LoadingState, StatusBadge },
  setup() {
    const route = useRoute()
    const app = ref(null)
    const loading = ref(true)
    const error = ref('')

    const entryIndex = computed(() => Number(app.value?.job_entry_index) || 0)

    const entrySalary = computed(() => {
      const parts = splitJobValues(app.value?.job_salary).filter(Boolean)
      return parts[entryIndex.value] || parts[0] || '—'
    })

    const entrySector = computed(() => {
      const parts = splitJobValues(app.value?.job_sector).filter(Boolean)
      return parts[entryIndex.value] || parts[0] || '—'
    })

    const cvFiles = computed(() => mapCvFilesForDisplay(app.value))

    function formatDate(value) {
      return value ? new Date(value).toLocaleString() : ''
    }

    async function load() {
      loading.value = true
      try {
        const res = await apiFetch(`/applications/${route.params.id}`)
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.message || 'Not found')
        app.value = data.application
      } catch (err) {
        error.value = err.message
      } finally {
        loading.value = false
      }
    }

    onMounted(load)
    return { app, loading, error, cvFiles, formatDate, getJobEntryTitle, entrySalary, entrySector }
  },
}
</script>
